// apps/prompts/src/hooks/useNotifications.ts
import { useAuthStore } from '@neuroforge/shared/client'
import { toast } from '@neuroforge/ui'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import {
  archiveNotification,
  fetchNotifications,
  fetchUnreadNotificationCount,
  markAllNotificationsRead,
  markNotificationRead,
  markNotificationUnread,
  type NotificationStatusFilter,
} from '../lib/api-prompts'

const notifKeys = {
  all: ['notifications'] as const,
  list: (page: number, status?: NotificationStatusFilter) =>
    [...notifKeys.all, 'list', page, status ?? 'ALL'] as const,
  unreadCount: () => [...notifKeys.all, 'unread-count'] as const,
}

export function useNotifications(page = 1, status?: NotificationStatusFilter) {
  const { isAuthenticated } = useAuthStore()
  const query = useQuery({
    queryKey: notifKeys.list(page, status),
    queryFn: () => fetchNotifications(page, 20, status),
    enabled: isAuthenticated,
    staleTime: 30_000,
    refetchInterval: 60_000,
  })

  const notifications = query.data?.data ?? []
  // Compteur local de la page courante uniquement. Pour la source de vérité globale,
  // utiliser useUnreadCount() qui interroge /notifications/unread-count.
  const unreadCount = notifications.filter((n) => n.status === 'UNREAD').length

  return {
    ...query,
    unreadCount,
  }
}

export function useUnreadCount() {
  const { isAuthenticated } = useAuthStore()
  return useQuery({
    queryKey: notifKeys.unreadCount(),
    queryFn: fetchUnreadNotificationCount,
    enabled: isAuthenticated,
    staleTime: 30_000,
    refetchInterval: 60_000,
  })
}

function invalidateNotifications(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: notifKeys.all })
}

export function useMarkRead() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: markNotificationRead,
    onSuccess: () => invalidateNotifications(qc),
    onError: () => {
      toast({ type: 'error', message: 'Erreur lors du marquage de la notification' })
    },
  })
}

export function useMarkUnread() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: markNotificationUnread,
    onSuccess: () => invalidateNotifications(qc),
    onError: () => {
      toast({ type: 'error', message: 'Erreur lors du marquage en non lu' })
    },
  })
}

export function useArchiveNotification() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: archiveNotification,
    onSuccess: () => invalidateNotifications(qc),
    onError: () => {
      toast({ type: 'error', message: "Erreur lors de l'archivage de la notification" })
    },
  })
}

export function useMarkAllRead() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: markAllNotificationsRead,
    onSuccess: () => invalidateNotifications(qc),
    onError: () => {
      toast({ type: 'error', message: 'Erreur lors du marquage des notifications' })
    },
  })
}
